import type { ReactNode } from "react";
import { allDocPages } from "./content";
import DocsLayout from "./layout";

const markdown = `
The Search API is a single endpoint that runs **vector** (semantic) and **full-text** (keyword) queries across every dataset CloudQuote indexes — equities, news, fundamentals, corporate events, macro indicators, and more.

It's designed for two situations:

1. You don't know the exact ticker, headline, or document ID, but you know the *concept* you're looking for.
2. You want one ranked list across multiple data domains.

## How it works

CloudQuote indexes data in two parallel ways:

- **Embedding index.** Headlines, article bodies, company profiles, and even price-series get encoded as vectors and stored in a managed vector database. Similarity search returns documents that are *semantically* close to your query.
- **Full-text index.** Text fields (headlines, bodies, profiles, filings) are stored in an inverted index for traditional boolean and phrase queries.

Each query can use either index alone or — most commonly — combine them with hybrid ranking.

## Endpoint: \`POST /search/query\`

### Request

\`\`\`json
{
  "vectorQuery": {
    "query": "semiconductor stocks with recent revenue growth",
    "model": "finance-embedding-v2",
    "topK": 10
  },
  "fullTextQuery": {
    "query": "\\"supply chain\\" AND (NVIDIA OR AMD)",
    "fields": ["headline", "body", "companyProfile"],
    "operator": "AND"
  },
  "filters": {
    "datasets": ["news", "equities"],
    "sectors": ["Technology"],
    "regions": ["USA"],
    "dateRange": { "start": "2025-05-01", "end": "2025-05-30" }
  },
  "rankBy": "hybrid",
  "format": "json"
}
\`\`\`

### Response

\`\`\`json
{
  "results": [
    {
      "documentId": "abc123",
      "dataset": "equities",
      "symbol": "AMD",
      "score": 0.94,
      "snippet": "AMD's revenue grew 35% QoQ, driven by datacenter GPU sales..."
    },
    {
      "documentId": "def456",
      "dataset": "news",
      "headline": "NVIDIA Partners with TSMC for Advanced Chips",
      "date": "2025-05-28T14:20:00Z",
      "score": 0.91,
      "url": "https://newswire.example.com/article/..."
    }
  ],
  "totalMatches": 253,
  "facets": {
    "datasets": { "equities": 180, "news": 73 },
    "sectors": { "Technology": 120, "Semiconductors": 80 }
  }
}
\`\`\`

## Ranking modes

Set \`rankBy\` to one of:

| Mode | When to use it |
| :---- | :---- |
| \`vector\` | Pure semantic similarity — "find things *like* this". |
| \`fulltext\` | Strict keyword match — when you need a specific phrase or boolean expression. |
| \`hybrid\` | Combine the two. Good default; balances semantic recall with keyword precision. |

## Filters

Every query can be narrowed by:

- \`datasets\` — limit to one or more domains (\`news\`, \`equities\`, \`fundamentals\`, \`events\`, \`macro\`).
- \`sectors\` — e.g., \`Technology\`, \`Healthcare\`.
- \`regions\` — e.g., \`USA\`, \`Asia\`.
- \`dateRange\` — only documents within \`{ start, end }\`.

## Pagination

Use \`page\` and \`pageSize\` for paginated retrieval:

\`\`\`json
{
  "fullTextQuery": { "query": "earnings beat", "fields": ["headline"] },
  "filters": { "datasets": ["news"] },
  "page": 2,
  "pageSize": 25
}
\`\`\`

## Multimodal search

The endpoint also accepts examples instead of text:

- A **chart image** — find instruments with similar price patterns.
- A **PDF excerpt or article snippet** — find related news or filings.

For multimodal queries, send the example as a base64-encoded payload in \`vectorQuery.exampleData\` along with \`vectorQuery.exampleType\` (\`image/png\`, \`application/pdf\`, etc.).

## Recipes

### Find companies similar to a target

\`\`\`json
{
  "vectorQuery": { "query": "companies similar to TSMC in semiconductor manufacturing", "topK": 5 },
  "filters": { "datasets": ["equities", "fundamentals"] }
}
\`\`\`

### Topical news monitoring

\`\`\`json
{
  "vectorQuery": { "query": "supply chain disruptions affecting semiconductor factories", "topK": 25 },
  "filters": { "datasets": ["news"], "dateRange": { "start": "2025-05-01", "end": "2025-05-30" } }
}
\`\`\`

### Combined keyword + semantic

\`\`\`json
{
  "vectorQuery": { "query": "EV battery startups raising Series B", "topK": 50 },
  "fullTextQuery": { "query": "Series B AND battery", "fields": ["headline", "body"] },
  "rankBy": "hybrid"
}
\`\`\`

## Where to go next

- See [Building AI Agents](/docs/agents) for examples that chain Search → Quote → News.
- The [Chat API](/docs/chat-api) lets an LLM call \`/search/query\` for you in plain English.
`;

export default function SearchApiPage(): ReactNode {
  const page = allDocPages.find(p => p.slug === "search-api")!;
  return <DocsLayout page={page} markdown={markdown}/>;
}
